/**
 * /marketplace/travel-insurance — editorial landing page for
 * Allianz travel-insurance affiliate (AgentMax embed).
 *
 * Public route (no auth gate) so search engines index it and anon
 * visitors can complete a quote.
 *
 * 2026-07-19 — Migrated from the previous provider to Allianz. The CTA is now
 * an embedded AgentMax iframe (widgetid=1269963, accam=F216709);
 * Allianz handles conversion tracking on their side. Our own
 * `POST /api/insurance-clicks/outbound` is fired once on mount with
 * source=`marketplace_iframe_view` for internal funnel analytics.
 */
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, CheckCircle2 } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { useCertMode } from "@/hooks/useCertMode";

const PAGE_TITLE = "Travel Insurance for Every Trip | TravelBio";
const PAGE_DESC =
  "Get an instant quote from Allianz Global Assistance. Protect your trip from cancellations, medical emergencies, and more with TravelBio's insurance marketplace.";
const OG_IMAGE =
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80&auto=format&fit=crop";

// AgentMax widget URL — user-supplied 2026-07-19. Kept verbatim
// (widgetid + accam + code are single-tenant to MyTravelBio).
const ALLIANZ_IFRAME_SRC =
  "https://www.agentmaxonline.com/agentmaxweb/storefront/index.html#/quotemax?widgetid=1269963&accam=F216709&code=ABIYU4TLWGBGTNHC6ZWLRSKAR65GB6C5JLBJOIXR7QY3M6I5HDDJUH2NNBT3OZ3AKCQQEDOSEX3H2SPID5G2WO67BECP4ZRXERW6KQQ6U45HAN52P7VQG5BWSIDOTDW3";

const TRUST_MARKERS = [
  { label: "Trusted by millions of travelers" },
  { label: "A-rated by A.M. Best" },
  { label: "24/7 travel assistance" },
  { label: "Personalized quote in under a minute" },
];

const FAQS = [
  {
    q: "What benefits can travel insurance plans provide?",
    a: "Depending on the plan selected, travel insurance can provide benefits for covered trip cancellations, trip interruptions, travel delays, baggage delays, lost or damaged baggage, emergency medical care, emergency medical transportation, and more. Benefits are available only when your situation meets the terms of your plan, so be sure to review your plan documents for complete details.",
  },
  {
    q: "Is travel protection right for my trip?",
    a: "Every traveler has different needs. Travel protection may be especially valuable if you've prepaid for non-refundable travel, are taking an international trip, cruising, traveling during severe weather seasons, or simply want added peace of mind. Unexpected events don't happen often—but having a plan in place can make them easier to manage if they do.",
  },
  {
    q: "When should I purchase travel protection?",
    a: "Many travelers choose to purchase travel protection shortly after making their initial trip deposit or booking. Buying early may provide access to additional plan benefits, depending on the plan selected. You can purchase a plan at different stages before your departure, but available benefits may vary.",
  },
  {
    q: "Why does TravelBio offer travel protection?",
    a: "TravelBio partners with Allianz Global Assistance to make it easy for travelers to compare travel protection plans while planning their trips. If you purchase an eligible plan through TravelBio, we may earn a commission at no additional cost to you. This helps support the continued growth and development of our platform while keeping your price the same.",
  },
  {
    q: "Can I compare different plans before purchasing?",
    a: "Yes. The quote tool allows you to review available travel protection plans based on your trip details. You can compare benefits, pricing, and plan features to help determine which option best fits your travel needs before making a purchase.",
  },
  {
    q: "Are pre-existing medical conditions covered?",
    a: "Some plans may include benefits related to pre-existing medical conditions when certain eligibility requirements are met. Terms, conditions, and exclusions apply. Please review your plan documents carefully for complete details.",
  },
  {
    q: "What if I need help while I'm traveling?",
    a: "Depending on your plan, you may have access to 24-hour travel assistance through Allianz Global Assistance. Assistance services can help with eligible travel-related situations such as finding medical providers, replacing lost travel documents, coordinating emergency assistance, and other travel support services available under your plan.",
  },
  {
    q: "How do I file a claim?",
    a: "Claims are handled directly by Allianz Global Assistance. If you need to submit a claim, follow the instructions included with your plan documents or visit Allianz's claims portal. Keeping receipts, booking confirmations, and other travel documentation can help support your claim.",
  },
];

function useDocumentHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const meta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.head.querySelector(sel);
      let created = false;
      if (!tag) {
        tag = document.createElement("meta");
        if (prop) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
        created = true;
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return () => {
        if (created) tag.remove();
        else if (prev != null) tag.setAttribute("content", prev);
      };
    };

    const cleanups = [
      meta("description", PAGE_DESC),
      meta("og:title", PAGE_TITLE, true),
      meta("og:description", PAGE_DESC, true),
      meta("og:image", OG_IMAGE, true),
      meta("og:type", "website", true),
      meta("twitter:card", "summary_large_image"),
      meta("twitter:title", PAGE_TITLE),
      meta("twitter:description", PAGE_DESC),
    ];
    return () => {
      document.title = prevTitle;
      cleanups.forEach((c) => c && c());
    };
  }, []);
}

export default function MarketplaceTravelInsurancePage() {
  useDocumentHead();
  // Phase 4 (2026-07-27): Hotelbeds cert isolation. When active,
  // replace the Allianz iframe (and its editorial copy) with a
  // small centered note so the page still resolves — a blank
  // iframe during cert review would read as a broken page.
  const { active: certMode } = useCertMode();

  // Fire the "iframe view" analytics ping exactly once per mount.
  // Fire-and-forget — the endpoint is best-effort funnel logging and
  // must not affect page render (no toast on error either; Toaster
  // is unmounted globally).
  const pingedRef = useRef(false);
  useEffect(() => {
    if (pingedRef.current) return;
    if (certMode) return;   // skip analytics while widget is hidden
    pingedRef.current = true;
    api.post("/insurance-clicks/outbound", { source: "marketplace_iframe_view" })
      // eslint-disable-next-line no-console
      .catch((e) => console.error("[insurance] iframe view ping failed", e));
  }, [certMode]);

  if (certMode) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col"
        data-testid="marketplace-travel-insurance-cert"
      >
        <div className="sticky top-0 z-30 bg-tb-navy-deep border-b-2 border-tb-gold/70 px-4 py-2 flex items-center gap-3">
          <Link
            to="/"
            data-testid="marketplace-back"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-gold/50 text-tb-cream hover:bg-tb-gold/10 tb-tap"
            style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.85rem" }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to TravelBio
          </Link>
          <span className="font-serif-it italic text-tb-gold-bright text-lg tracking-wide ml-2">
            Marketplace · Travel Insurance
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-24">
          <div
            data-testid="mkt-insurance-cert-placeholder"
            className="max-w-md w-full rounded-2xl bg-tb-navy-deep text-white px-6 py-8 text-center shadow-xl"
          >
            <Shield className="w-6 h-6 mx-auto mb-3 text-white/80" />
            <p className="font-serif-it italic text-xl mb-2">
              Travel insurance temporarily hidden
            </p>
            <p className="font-hand text-sm text-white/80 leading-relaxed">
              This section is hidden for Hotelbeds inventory review.
              Please contact TravelBio if you need to view it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="marketplace-travel-insurance">
      {/* Slim top bar with a back link + brand. Public route, so no
          AppShell — this page renders standalone. */}
      <div className="sticky top-0 z-30 bg-tb-navy-deep border-b-2 border-tb-gold/70 px-4 py-2 flex items-center gap-3">
        <Link
          to="/"
          data-testid="marketplace-back"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-gold/50 text-tb-cream hover:bg-tb-gold/10 tb-tap"
          style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.85rem" }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to TravelBio
        </Link>
        <span
          className="font-serif-it italic text-tb-gold-bright text-lg tracking-wide ml-2"
        >
          Marketplace · Travel Insurance
        </span>
      </div>

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: "clamp(320px, 42vh, 460px)" }}
      >
        <img
          src={OG_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,58,95,0.55) 0%, rgba(26,58,95,0.82) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-4xl mx-auto text-center px-4 py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-tb-gold/15 border border-tb-gold/50 px-3 py-1 text-tb-gold-bright mb-6">
            <Shield className="w-4 h-4" />
            <span className="font-hand text-xs uppercase tracking-[0.22em]">
              Travel Insurance
            </span>
          </div>
          <h1
            data-testid="mkt-insurance-title"
            className="font-serif-it italic leading-[1.02] drop-shadow-lg"
            style={{
              color: "#f5f0e6",
              fontSize: "clamp(2.5rem, 5.4vw, 4.25rem)",
              textWrap: "balance",
            }}
          >
            Protect Your Trip
          </h1>
          <p
            className="mt-5 font-serif-it italic max-w-2xl mx-auto"
            style={{
              color: "rgba(245,240,230,0.9)",
              fontSize: "clamp(1rem, 1.8vw, 1.35rem)",
            }}
          >
            Get an instant quote from Allianz Global Assistance.
          </p>
        </div>
      </section>

      {/* Trust markers */}
      <section className="border-b border-tb-cream-3 bg-white" data-testid="mkt-insurance-trust">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-4">
          {TRUST_MARKERS.map(({ label }) => (
            <span
              key={label}
              data-testid="mkt-insurance-trust-item"
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-tb-navy/15 text-tb-navy px-3 py-1.5 font-hand text-xs sm:text-sm"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.2} />
              {label}
            </span>
          ))}
        </div>
      </section>

      {/* Editorial body — 2026-07-27 rewritten to Allianz-compliant copy. */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14 space-y-5">
        <h2 data-testid="mkt-insurance-protect-heading" className="font-serif-it italic text-tb-navy text-2xl md:text-3xl">
          Protect Your Trip
        </h2>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          Travel is full of unforgettable moments&mdash;but even the best-planned adventures can take an unexpected turn. A delayed flight, severe weather, lost baggage, or a covered illness could disrupt your plans and lead to unexpected expenses.
        </p>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          TravelBio offers travel protection plans from Allianz Global Assistance to help give travelers greater confidence before and during their trip. Depending on the plan selected, travel insurance can provide benefits for covered trip cancellations, trip interruptions, travel delays, baggage issues, emergency medical situations, and more. Terms, conditions, and exclusions apply, including for pre-existing conditions.
        </p>

        <h3 data-testid="mkt-insurance-why-heading" className="font-serif-it italic text-tb-navy text-xl md:text-2xl pt-4">
          Why many travelers choose travel protection
        </h3>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          From the moment you leave home until you return, unexpected events can happen. The right travel protection plan may provide benefits that help reimburse eligible, prepaid, non-refundable trip expenses if your trip is affected by a covered reason. Plans can also provide benefits for covered travel delays, baggage delays, lost or damaged personal belongings, emergency medical care, and emergency medical transportation while traveling.
        </p>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          Whether you&rsquo;re planning a weekend getaway, a cruise, or an international adventure, travel protection can provide added peace of mind when the unexpected happens.
        </p>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          TravelBio offers travel protection plans from Allianz Global Assistance, one of the most recognized names in travel protection. Their secure quote tool makes it easy to compare available plans and select the option that best fits your trip.
        </p>
        <p className="font-hand text-tb-navy/85 text-lg leading-relaxed">
          Use the quote tool below to receive a personalized quote today.
        </p>
        <p className="font-hand text-tb-navy/70 text-base leading-relaxed italic">
          Terms, conditions, and exclusions apply, including for pre-existing conditions. Please review your plan documents carefully for complete details on benefits, limitations, and covered reasons.
        </p>
      </section>

      {/* Primary CTA — Allianz AgentMax iframe. Fixed 300×700 as
          provided by Allianz; wrapper centers it and prevents
          horizontal overflow on narrow screens.
          2026-07-27 — added `allow="popups; popups-to-escape-sandbox"`
          because AgentMax opens quote results in a new window on
          "Get Quotes". Without the popup allowance the click showed
          a spinner and then a blank iframe. Also shipping an
          explicit "Open in new tab" fallback link below so users
          hitting stricter popup policies still have a working path. */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 pb-10">
        <div
          data-testid="mkt-insurance-iframe-wrap"
          className="rounded-2xl border-2 border-tb-cream-3 bg-white shadow-sm overflow-hidden"
          style={{ maxWidth: "100%" }}
        >
          <div className="flex justify-center py-6 px-2" style={{ overflow: "hidden" }}>
            <iframe
              title="Allianz Travel Insurance quote"
              data-testid="mkt-insurance-iframe"
              src={ALLIANZ_IFRAME_SRC}
              width="300"
              height="700"
              frameBorder="0"
              allow="clipboard-read; clipboard-write; payment"
              style={{ maxWidth: "100%", border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
        <p className="mt-3 font-hand text-tb-navy/55 text-xs text-center italic">
          Powered by Allianz Global Assistance · quotes served securely by
          AgentMax
        </p>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 pb-14" data-testid="mkt-insurance-faq">
        <h2 className="font-serif-it italic text-tb-navy text-2xl md:text-3xl mb-4">
          Travel Insurance FAQs
        </h2>
        <Accordion type="multiple" className="rounded-2xl border-2 border-tb-cream-3 bg-white overflow-hidden">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={String(i)} className="border-tb-cream-3 last:border-b-0">
              <AccordionTrigger className="px-5 py-3 font-serif-it text-lg text-tb-navy hover:no-underline text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 font-hand text-tb-navy/80 text-base leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Important note — Allianz-mandated disclosure. */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 pb-14" data-testid="mkt-insurance-important-note">
        <div className="rounded-2xl bg-tb-navy/[0.03] border border-tb-navy/15 px-5 py-4">
          <p className="font-hand text-tb-navy/85 text-sm md:text-base leading-relaxed">
            <span className="font-semibold text-tb-navy">Important note:</span>
            {" "}Travel protection plans include insurance benefits and assistance services. Terms, conditions, and exclusions apply, including for pre-existing conditions. Please review your plan documents carefully for complete details before purchasing.
          </p>
        </div>
      </section>

      {/* Footer disclosure */}
      <footer className="bg-tb-navy text-tb-cream/80 border-t-2 border-tb-gold/70 px-4 py-6 text-center">
        <p className="font-hand text-tb-gold/70 text-sm max-w-2xl mx-auto italic">
          TravelBio earns a small commission when you purchase a plan through Allianz Global Assistance &mdash; your price is unchanged. We only partner with companies we trust.
        </p>
        <p className="mt-3 font-hand text-tb-gold/40 text-xs">
          © TravelBio · your lifelong travel journal
        </p>
      </footer>
    </div>
  );
}
